import { DevToolsStatusDot } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/DevToolsStatusDot';
import type { DevToolsTabBarProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/DevToolsTabBar/index.types';
import { TAB_ITEMS } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/index.constants';
import React from 'react';
import { TabsList, TabsTrigger } from '~/lib/ui/tabs';
import { cn } from '~/lib/utils';

/**
 * DevToolsTabBar - The six tabs, as an underline rather than a folder
 *
 * The active tab is marked by a 2px accent bar inset 6px from each edge,
 * drawn as its own absolutely-positioned element rather than a bottom
 * border:
 */
export const DevToolsTabBar: React.FC<DevToolsTabBarProps> = ({
  statusByTab,
  countByTab,
}) => (
  <TabsList className="h-[38px] w-full flex-shrink-0 items-stretch justify-start gap-0.5 rounded-none border-b border-zinc-800 bg-zinc-900 px-2 py-0">
    {TAB_ITEMS.map(({ value, label }) => {
      const status = statusByTab[value];
      const count = countByTab[value];

      return (
        <TabsTrigger
          key={value}
          value={value}
          className={cn(
            'group relative h-auto flex-none cursor-pointer gap-1.5 rounded-none border-0 px-2.5 py-0',
            'text-[12.5px] font-medium whitespace-nowrap',
            'text-zinc-400 hover:text-zinc-200',
            // The shared trigger's focus ring is the app's light `--ring`, which
            // reads as a halo on a dark panel. The panel supplies its own.
            'focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40',
            'data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-zinc-200 data-[state=active]:shadow-none'
          )}
        >
          {status && <DevToolsStatusDot status={status} />}
          {label}
          {count !== undefined && count > 0 && (
            <span className="rounded bg-zinc-800 px-1 font-sp-mono text-[10px] text-zinc-300">
              {count}
            </span>
          )}
          <span className="absolute inset-x-1.5 -bottom-px hidden h-0.5 rounded-sm bg-emerald-400 group-data-[state=active]:block" />
        </TabsTrigger>
      );
    })}
  </TabsList>
);
